import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

import type {
  FilestackMultiuploadClient,
  FilestackMultiuploadProgressEvent,
  FilestackUploadControlToken,
} from 'src/helpers/integrations/filestack-multiupload';
import {
  getIsFailedMultiuploadFile,
  multiuploadWithAuthRetry,
} from 'src/helpers/integrations/filestack-multiupload';
import type { FilestackUploadPayload } from 'src/helpers/integrations/filestack-upload-payload';
import { filestackUploadPayloadSchema } from 'src/helpers/integrations/filestack-upload-payload';
import { Sentry } from 'src/sentry';

import {
  PROGRESS_THROTTLE_MS,
  REPOSITORY_DROPZONE_SENTRY_FEATURE,
  UPLOAD_PROGRESS_MAX_WHILE_UPLOADING,
} from '../../RepositoryDropzoneConfig';
import type { UploadFile } from '../../useRepositoryDropzone';

export interface RepositoryDropzoneUploadStackRow {
  id: string;
  result: FilestackUploadPayload | null;
}

interface RunRepositoryDropzoneMultiuploadBatchParams {
  abortedIdsRef: MutableRefObject<Set<string>>;
  fileRefsRef: MutableRefObject<Map<string, File>>;
  filestackClient: FilestackMultiuploadClient;
  getFreshFilestackClient: () => Promise<FilestackMultiuploadClient>;
  items: UploadFile[];
  setUploadFiles: Dispatch<SetStateAction<UploadFile[]>>;
  /** Receives batch-level pause/resume/cancel controls once the multiupload starts. */
  token: FilestackUploadControlToken;
  /** Start the batch paused; set when the user paused before the batch reached the SDK. */
  startPaused?: boolean;
}

/**
 * Uploads the given rows as a single Filestack multiupload batch, mapping the SDK's per-file
 * progress and statuses back into the existing upload rows. The SDK owns multipart scheduling,
 * part concurrency, and transport retries across the whole batch; one file failing does not fail
 * the rest. Returns one row per input item, in input order, with the parsed upload payload for
 * successes and null otherwise.
 */
export async function runRepositoryDropzoneMultiuploadBatch(
  params: RunRepositoryDropzoneMultiuploadBatchParams
): Promise<RepositoryDropzoneUploadStackRow[]> {
  const {
    abortedIdsRef,
    fileRefsRef,
    filestackClient,
    getFreshFilestackClient,
    items,
    setUploadFiles,
    startPaused,
    token,
  } = params;

  const batchFiles: File[] = [];
  const missingRefIds: string[] = [];
  const batchItems: UploadFile[] = [];
  const patchById = new Map<string, Partial<UploadFile>>();
  const resultById = new Map<string, FilestackUploadPayload | null>();

  for (const item of items) {
    resultById.set(item.id, null);

    if (abortedIdsRef.current.has(item.id)) {
      abortedIdsRef.current.delete(item.id);
      patchById.set(item.id, { state: 'cancelled' });
      continue;
    }

    const file = fileRefsRef.current.get(item.id);
    if (!file) {
      missingRefIds.push(item.id);
      patchById.set(item.id, { error: 'File not found', state: 'failed' });
      continue;
    }

    batchItems.push(item);
    batchFiles.push(file);
    patchById.set(item.id, { state: 'uploading' });
  }

  if (missingRefIds.length) {
    Sentry.captureException(new Error(`File refs not found for ids: ${missingRefIds.join(', ')}`), {
      tags: { feature: REPOSITORY_DROPZONE_SENTRY_FEATURE },
    });
  }

  patchUploadRows(setUploadFiles, patchById);
  if (!batchItems.length) return items.map(item => ({ id: item.id, result: null }));

  /**
   * The SDK keys per-file progress by an internal upload id whose entries appear in input order
   * but drop out as files finish, so a key's position within a single event is not a stable
   * index. Assign each key the next batch index on first appearance instead. The ids are minted
   * per multiupload attempt, so the map is cleared when the auth retry starts a new attempt.
   */
  const progressKeyToIndex = new Map<string, number>();
  function handleProgress(evt: FilestackMultiuploadProgressEvent) {
    const progressByFileId = new Map<string, number>();

    if (evt.files) {
      for (const [key, fileProgress] of Object.entries(evt.files)) {
        if (!progressKeyToIndex.has(key)) progressKeyToIndex.set(key, progressKeyToIndex.size);
        const index = progressKeyToIndex.get(key);
        const item = index === undefined ? undefined : batchItems[index];
        if (item) progressByFileId.set(item.id, fileProgress.totalPercent);
      }
    } else {
      // Per-file progress is unavailable; degrade to aggregate progress for every batch row.
      batchItems.forEach(item => progressByFileId.set(item.id, evt.totalPercent));
    }

    setUploadFiles(prev =>
      prev.map(f => {
        const percent = progressByFileId.get(f.id);
        if (percent === undefined || f.state !== 'uploading') return f;
        if (abortedIdsRef.current.has(f.id)) return f;
        return { ...f, progress: Math.min(percent, UPLOAD_PROGRESS_MAX_WHILE_UPLOADING) };
      })
    );
  }

  const uploadOutcome = await multiuploadWithAuthRetry({
    files: batchFiles,
    filestackClient,
    getFreshFilestackClient,
    onAuthRetry: () => progressKeyToIndex.clear(),
    onProgress: handleProgress,
    progressInterval: PROGRESS_THROTTLE_MS,
    startPaused,
    token,
  }).then(
    results => ({ ok: true as const, results }),
    (error: unknown) => ({ error, ok: false as const })
  );
  if (!uploadOutcome.ok) {
    Sentry.captureException(uploadOutcome.error, {
      tags: { feature: REPOSITORY_DROPZONE_SENTRY_FEATURE },
    });
    const failurePatches = new Map<string, Partial<UploadFile>>();
    const message =
      uploadOutcome.error instanceof Error ? uploadOutcome.error.message : 'Upload failed.';

    for (const item of batchItems) {
      if (abortedIdsRef.current.has(item.id)) {
        abortedIdsRef.current.delete(item.id);
        failurePatches.set(item.id, { state: 'cancelled' });
      } else {
        failurePatches.set(item.id, { error: message, state: 'failed' });
      }
    }

    patchUploadRows(setUploadFiles, failurePatches);

    return items.map(item => ({ id: item.id, result: null }));
  }

  // Multiupload resolves one SDK file object per input file, in input order.
  const completionPatches = new Map<string, Partial<UploadFile>>();
  batchItems.forEach((item, index) => {
    if (abortedIdsRef.current.has(item.id)) {
      abortedIdsRef.current.delete(item.id);
      completionPatches.set(item.id, { state: 'cancelled' });
      return;
    }

    const raw = uploadOutcome.results[index];
    if (getIsFailedMultiuploadFile(raw)) {
      Sentry.captureException(new Error(`Multiupload file failed: ${item.filename}`), {
        tags: { feature: REPOSITORY_DROPZONE_SENTRY_FEATURE },
      });
      completionPatches.set(item.id, { error: 'Upload failed.', state: 'failed' });
      return;
    }

    const parsed = filestackUploadPayloadSchema.safeParse(raw);
    if (!parsed.success) {
      completionPatches.set(item.id, {
        error: 'Uploaded file returned unexpected data from storage.',
        state: 'failed',
      });
      return;
    }

    resultById.set(item.id, parsed.data);
    completionPatches.set(item.id, { progress: 100, state: 'persisting' });
  });

  patchUploadRows(setUploadFiles, completionPatches);

  return items.map(item => ({ id: item.id, result: resultById.get(item.id) ?? null }));
}

function patchUploadRows(
  setUploadFiles: Dispatch<SetStateAction<UploadFile[]>>,
  patchById: Map<string, Partial<UploadFile>>
) {
  if (!patchById.size) return;

  setUploadFiles(prev =>
    prev.map(f => {
      const patch = patchById.get(f.id);
      return patch ? { ...f, ...patch } : f;
    })
  );
}
